import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Camera, CameraOff, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { errMsg } from "@/lib/api";
import { playNewOrder } from "@/lib/sound";

const RESULT = {
  valid: ["bg-brand text-white", CheckCircle2, "VALID — let them in"],
  used: ["bg-ready text-ink", AlertTriangle, "ALREADY USED"],
  invalid: ["bg-danger text-white", XCircle, "INVALID PASS"],
};

const ResultPanel = ({ r }) => {
  const [cls, Icon, title] = RESULT[r.result];
  return (
    <div className={`rounded-3xl p-6 text-center ${cls}`} data-testid="scan-result" data-result={r.result}>
      <Icon size={56} className="mx-auto" />
      <div className="mt-2 font-heading text-3xl font-black" data-testid="scan-result-title">{title}</div>
      <div className="mt-1 font-mono tracking-widest">{r.code}</div>
      {r.holder && <div className="mt-2 font-semibold">{r.holder} · {r.ticket_name}</div>}
      {r.result === "used" && r.used_at && <div className="text-sm">Scanned {new Date(r.used_at).toLocaleTimeString("en-CA")} by {r.used_by}</div>}
    </div>
  );
};

const EventCounts = ({ events }) => (
  <div className="grid gap-2" data-testid="event-checkin-counts">
    {events.map((e) => <div key={e.id} className="flex items-center justify-between rounded-2xl bg-brand-pale p-4">
      <div><div className="font-heading font-extrabold">{e.name}</div><div className="text-xs text-muted2">{e.date} · {e.issued} {e.issued === 1 ? "pass" : "passes"} issued</div></div>
      <div className="font-heading text-2xl font-black" data-testid={`checkin-count-${e.id}`}>{e.checked_in} / {e.capacity}</div></div>)}
  </div>
);

export default function DoorScanner({ api }) {
  const [result, setResult] = useState(null);
  const [manual, setManual] = useState("");
  const [camOn, setCamOn] = useState(false);
  const [events, setEvents] = useState([]);
  const scanner = useRef(null);
  const busy = useRef(false);
  const loadEvents = () => api.get("/events").then((r) => setEvents(r.data)).catch(() => {});

  const check = async (code) => {
    if (busy.current || !code) return;
    busy.current = true;
    try { const r = await api.post("/scan", { code }); setResult(r.data); if (r.data.result === "valid") playNewOrder(); loadEvents(); }
    catch (e) { toast.error(errMsg(e)); }
    setTimeout(() => { busy.current = false; }, 2000);
  };
  const stop = async () => { try { await scanner.current?.stop(); } catch {} setCamOn(false); };
  const start = async () => {
    try {
      scanner.current = scanner.current || new Html5Qrcode("qr-reader");
      await scanner.current.start({ facingMode: "environment" }, { fps: 10, qrbox: 240 }, (txt) => check(txt.trim()));
      setCamOn(true);
    } catch (e) { toast.error("Camera unavailable — type the pass code instead."); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadEvents(); return () => { stop(); }; }, []);

  return (
    <div className="mx-auto grid max-w-4xl gap-4 md:grid-cols-2" data-testid="door-scanner">
      <div className="grid content-start gap-3">
        <div id="qr-reader" className="overflow-hidden rounded-3xl bg-ink/5" style={{ minHeight: camOn ? 260 : 0 }} />
        <button className={camOn ? "btn-secondary" : "btn-primary"} onClick={camOn ? stop : start} data-testid="scanner-camera-btn">
          {camOn ? <><CameraOff size={18} /> Stop camera</> : <><Camera size={18} /> Scan with camera</>}</button>
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); busy.current = false; check(manual.trim()); setManual(""); }}>
          <Input placeholder="Or type pass code" value={manual} onChange={(e) => setManual(e.target.value.toUpperCase())} data-testid="scanner-manual-input" />
          <button className="btn-primary !px-5 !py-2" data-testid="scanner-manual-submit">Check</button>
        </form>
      </div>
      <div className="grid content-start gap-3">
        {result ? <ResultPanel r={result} /> : <div className="rounded-3xl border-2 border-dashed border-line p-8 text-center text-muted2">Scan a LineGone pass to check someone in.</div>}
        <EventCounts events={events} />
      </div>
    </div>
  );
}
